// Real backend client for the V3 student dashboard (claude-student-api).
// POST /student/api { action, ...params }. Returns real class assignments
// (due dates set by the teacher in ClaudeClassUnits) and the student's own
// activity (minutes this week + streak from ClaudeDailyActivity).

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../Accounts/AuthContext';
import { dueDateOf, dueEndMs } from '../../TeacherDashboardV3/data/unitPlan';
// Stage-aware endpoints (src/config/api.js): npm start -> Development, builds -> Production.
import { STUDENT_API_URL, USER_API_URL } from '../../config/api';
import { isOpenAssignmentSlot } from './assignmentSlots';
import { studentViewAsCall, viewingAs } from '../../ViewAs/viewAsApi';

export async function studentApi(auth, action, params = {}) {
  // Admin "view as" (2026-09-25): answered by the admin API as the viewed
  // student, read only; homework answers are graded in the browser.
  if (viewingAs('student')) return studentViewAsCall(auth, action, params);
  const { user, handleAPIResponse } = auth || {};
  const token = user?.idToken || user?.accessToken;
  if (!token) throw new Error('not signed in');
  const body = { action, ...params };
  let res = await fetch(STUDENT_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  });
  if ((res.status === 401 || res.status === 403) && handleAPIResponse) {
    res = await handleAPIResponse(res, body); // token refresh + retry
  }
  let data = null;
  try { data = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    // Same message as before; the HTTP status rides along so callers can tell a
    // genuine 404 from a transient failure. Existing callers are unaffected.
    const err = new Error(data?.error || `student api ${action} failed (${res.status})`);
    err.status = res.status;
    err.data = data; // error bodies can carry context (e.g. 'already joined' includes the class name)
    throw err;
  }
  return data;
}

export async function joinClass(auth, joinCode) {
  return studentApi(auth, 'joinClass', { joinCode });
}

// Every class I'm enrolled in: [{ classID, name, period, satDate }].
export async function fetchMyClasses(auth) {
  const r = await studentApi(auth, 'getMyClasses');
  return r.classes || [];
}

// ---- Custom homework (teacher-assigned practice sets) + reminders ----

// Every open homework set assigned to me, due-date ascending, with my progress.
// Closed sets (finished by the teacher, or closed at their due date) come back
// only when I have progress on them, flagged closed: true (2026-10-08);
// closeAtDue says an open set will close when its due date passes.
export async function fetchMyHomework(auth) {
  const r = await studentApi(auth, 'listMyHomework');
  return r.items || [];
}

// Start (or resume) a set: returns { hw, questions, progress, closed? }. A 409
// with no code means no questions are available for the set yet — callers
// show an honest empty state, never filler. closed: true = read-only review.
export async function startHomework(auth, classID, hwId) {
  return studentApi(auth, 'startHomework', { classID, hwId });
}

// Why a homework read or write was refused (2026-10-08, PLAN.md Part 3):
// 'closed' = the teacher closed it or its due date closed it (409 CLOSED);
// 'removed' = the teacher deleted it or took me off it (404 REMOVED);
// 'not-assigned' = it is for other students, never was mine (404
// NOT_ASSIGNED, 2026-10-24; the older backend says REMOVED for this too);
// 'not-open' = assigned to me but its start time hasn't come (409 NOT_OPEN,
// err.data.startAt; 2026-10-09); null = anything else. Pages show a friendly
// message for these, never a raw error.
export function homeworkRefusal(err) {
  const code = err?.data?.code;
  if (err?.status === 409 && code === 'CLOSED') return 'closed';
  if (err?.status === 404 && code === 'REMOVED') return 'removed';
  if (err?.status === 404 && code === 'NOT_ASSIGNED') return 'not-assigned';
  if (err?.status === 409 && code === 'NOT_OPEN') return 'not-open';
  return null;
}

// Server-graded, idempotent (a duplicate submit returns { ok, duplicate }).
// An OPEN assessment (2026-10-26): no verdict comes back; a different pick on
// a saved question replaces it ({ changed: true }) until submitAssessment, and
// after that every save is refused (409 { error: 'submitted', submittedAt }).
export async function submitHomeworkAnswer(auth, classID, hwId, recordID, selected, timeSec, integrity) {
  return studentApi(auth, 'submitHomeworkAnswer', {
    classID, hwId, recordID, selected, timeSec,
    // Since-last-answer deltas the player counted (tab switches, copy/paste);
    // shown to the teacher, never part of any score.
    tabAways: integrity?.tabAways || 0,
    copyPaste: integrity?.copyPaste || 0,
  });
}

// Hand in an open assessment (2026-10-26): every question must have an answer
// (else 409 { error: 'not all answered', missing }). Sets progress.submittedAt
// once (a repeat returns the same value): { ok, done, submittedAt }. A closed
// set answers 409 CLOSED.
export async function submitAssessment(auth, classID, hwId) {
  return studentApi(auth, 'submitAssessment', { classID, hwId });
}

// Persist where the student is inside a missed question's step walkthrough
// (2026-08-07): a wrong answer is not finished until the walk is, and leaving
// mid-walk must resume on the exact step. patch = { step } or { step, done }.
export async function updateHomeworkWalk(auth, classID, hwId, recordID, patch) {
  return studentApi(auth, 'updateHomeworkWalk', { classID, hwId, recordID, ...patch });
}

// Clear the teacher's nudge once the student has seen it.
export async function dismissReminder(auth, classID) {
  return studentApi(auth, 'dismissReminder', { classID });
}

// The unit's fixed 4-assignment shape (matches the teacher timeline).
const UNIT_TYPES = ['Lesson', 'Lesson', 'Mixed Practice', 'Skill Check'];

// assignment {dueDates[4], subject, domainKey} -> todo list the agenda renders.
// A due may be date-only ('2026-08-21', legacy) or a full ISO datetime
// (2026-08-07): `due` is the Date it renders as, `dueRaw` keeps the stored
// string so displays can show the time, `past` uses dueEndMs (a date-only due
// stays current through its whole local day).
export function todosOf(assignments) {
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const now = Date.now();
  const out = [];
  for (const a of assignments || []) {
    (a.dueDates || []).forEach((iso, i) => {
      const due = dueDateOf(iso);
      const dayStart = new Date(due); dayStart.setHours(0, 0, 0, 0);
      out.push({
        key: `${a.classID}-${a.unitSeq}-${i}`,
        type: UNIT_TYPES[i] || 'Assignment',
        due,
        dueRaw: iso,
        past: dueEndMs(iso) < now,
        daysLeft: Math.round((dayStart - todayStart) / 86400000),
        subject: a.subject === 'math' ? 'math' : 'reading',
        domainKey: a.domainKey,
      });
    });
  }
  return out.sort((x, y) => x.due - y.due);
}

// One agenda item per CLASS (Sandy 2026-08-07): the first slot the student
// hasn't finished yet — a queue, not a calendar. Finishing it surfaces the
// next; a class whose slots are all done (or N/A) contributes nothing.
// Uses the server's per-slot done flags (window + asgSeq), so "done early"
// drops off immediately, not at the due date.
export function nextTodosOf(assignments) {
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const now = Date.now();
  const out = [];
  for (const a of assignments || []) {
    const di = (a.slots || []).findIndex(isOpenAssignmentSlot);
    if (di < 0) continue;
    const s = a.slots[di];
    const due = dueDateOf(s.due);
    const dayStart = new Date(due); dayStart.setHours(0, 0, 0, 0);
    out.push({
      key: `${a.classID}-${a.unitSeq}-${di}`,
      classID: a.classID,
      // Placement gate (Sandy 2026-08-27): a unit in a domain the student has
      // no level in fronts its placement walk as the first task. The walk
      // borrows the first open slot's due date — it precedes that slot, so it
      // inherits its deadline rather than inventing one. The server derives
      // needsPlacement fresh each read; finishing the walk clears it.
      type: a.needsPlacement ? 'Find your level' : (s.label || UNIT_TYPES[di] || 'Assignment'),
      placement: !!a.needsPlacement,
      due,
      dueRaw: s.due,
      past: dueEndMs(s.due) < now,
      daysLeft: Math.round((dayStart - todayStart) / 86400000),
      subject: a.subject === 'math' ? 'math' : 'reading',
      domainKey: a.domainKey,
    });
  }
  return out.sort((x, y) => x.due - y.due);
}

// The student's own profile row (ClaudeUsers via claude-user-api): the home
// page needs `school` to decide how loudly to offer the join-a-class card.
export async function fetchMyProfile(auth) {
  const token = auth?.user?.idToken || auth?.user?.accessToken;
  if (!token) return null;
  const res = await fetch(USER_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ action: 'getProfile' }),
  });
  if (!res.ok) throw new Error(`getProfile failed (${res.status})`);
  const data = await res.json();
  return data.profile || null;
}

// The real unit structure (windows of 2 band-skills) for a domain+band —
// computed server-side from KnowledgeTracking with the engine's own algorithm.
export async function fetchDomainUnits(auth, domainKey, band) {
  const r = await studentApi(auth, 'getDomainUnits', { domainKey, band });
  return r.units || [];
}

// PSAT/NMSQT score report (Bluebook / PSAT tab, 2026-10-27). Saving places or
// moves domain levels server-side; the response says what happened per domain.
export async function fetchMyScoreReport(auth) {
  const r = await studentApi(auth, 'getMyScoreReport');
  return r.report || null;
}

export async function submitScoreReport(auth, { readingScore, mathScore, levels }) {
  const r = await studentApi(auth, 'submitScoreReport', { readingScore, mathScore, levels });
  return r.report;
}

// My real per-domain engine state (band = level). Returns { data, loading,
// error } so callers can tell "still loading" (show the cube) from "failed"
// (honest N/A) from "signed out" (data null, not loading -> demo mock).
// Placement self-heal: if an unplaced student HAS finished the diagnostic
// (submitted then closed the tab before the score page fired placement), any
// dashboard visit places them. Server-side idempotent; once per page load.
let placementAttempted = false;

export function useMyProgress() {
  const { user, handleAPIResponse } = useAuth();
  const auth = useMemo(() => ({ user, handleAPIResponse }), [user, handleAPIResponse]);
  const [state, setState] = useState({ data: null, loading: !!user, error: false });
  useEffect(() => {
    let alive = true;
    if (!user) { setState({ data: null, loading: false, error: false }); return undefined; }
    setState({ data: null, loading: true, error: false });
    studentApi(auth, 'getMyProgress')
      .then((r) => {
        if (!alive) return;
        setState({ data: r, loading: false, error: false });
        if (!r?.placed && !placementAttempted) {
          placementAttempted = true;
          studentApi(auth, 'placeFromDiagnostic')
            .then((p) => {
              // Bands landed just now: re-read so the dashboard shows them.
              if (alive && p && Object.keys(p.placed || {}).length) {
                return studentApi(auth, 'getMyProgress').then((r2) => {
                  if (alive) setState({ data: r2, loading: false, error: false });
                });
              }
              return null;
            })
            .catch(() => { /* diagnostic not done yet, or IAM pending */ });
        }
      })
      .catch(() => { if (alive) setState({ data: null, loading: false, error: true }); });
    return () => { alive = false; };
  }, [user, auth]);
  // data: { placed, domains: { algebra: {band, graduated, stuck, ...} } }
  return state;
}

// One hook for the student home: real agenda + activity. Same { data,
// loading, error } shape as useMyProgress — data stays null while loading and
// when signed out (signed-out callers fall back to the mock so demos keep
// working); error means BOTH fetches failed, so a signed-in page can show
// honest N/A values instead of the demo's numbers.
export function useStudentAgenda() {
  const { user, handleAPIResponse } = useAuth();
  const auth = useMemo(() => ({ user, handleAPIResponse }), [user, handleAPIResponse]);
  const [state, setState] = useState({ data: null, loading: !!user, error: false });
  useEffect(() => {
    let alive = true;
    if (!user) { setState({ data: null, loading: false, error: false }); return undefined; }
    setState({ data: null, loading: true, error: false });
    Promise.all([
      studentApi(auth, 'getAssignments').catch(() => null),
      studentApi(auth, 'getMyActivity').catch(() => null),
    ]).then(([assign, act]) => {
      if (!alive) return;
      const assignments = assign?.assignments || [];
      setState({
        loading: false,
        error: assign === null && act === null,
        data: {
          loaded: true,
          enrolled: assignments.length > 0,
          // The student's NEW-STACK school (ClaudeUsers via getAssignments,
          // Sandy 2026-08-10) — class-feature gating keys off this, not the
          // legacy UserDatabase school. null until the row exists.
          school: assign?.school ?? null,
          // New-stack goal score (ClaudeUsers via getAssignments, 2026-08-18).
          // null when unset — the home page falls back to legacy GoalScore.
          goalScore: assign?.goalScore ?? null,
          assignments,
          // Teacher nudges ({classID, at, message|null}); cleared via dismissReminder.
          reminders: assign?.reminders || [],
          todos: todosOf(assignments),
          // One per class: the first not-done slot (queue semantics).
          nextTodos: nextTodosOf(assignments),
          minutesWeek: act ? act.minutesWeek : null,
          streakDays: act ? act.streakDays : null,
        },
      });
    });
    return () => { alive = false; };
  }, [user, auth]);
  return state;
}
